<script setup lang="ts">
/**
 * El juego de iconos, hecho aquí mismo.
 *
 * Un puñito de trazos escritos a mano le gana a un paquete entero de iconos para
 * un tablero de un puñado de pantallas, y de paso el bundle se ahorra una
 * dependencia que traería mil glifos para pintar veinte.
 */
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    name: string;
    size?: number | string;
    strokeWidth?: number;
  }>(),
  { size: 18, strokeWidth: 1.7 },
);

const PATHS: Record<string, string> = {
  dashboard: "M4 13h6V4H4v9Zm0 7h6v-5H4v5Zm10 0h6v-9h-6v9Zm0-16v5h6V4h-6Z",
  target:
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-4.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0-3.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
  flow: "M5 6h5m4 0h5M5 18h5m4 0h5M7.5 8.5v7M16.5 8.5v7M10 6h4M10 18h4",
  agent:
    "M12 3v2m-7 3h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Zm3.5 5v2m6-2v2M9 16h6",
  runs: "M4 7h16M4 12h16M4 17h10",
  approvals: "m5 13 4.5 4.5L19 8M4 20h16",
  settings:
    "M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm8-3.5-.1-1.2 1.7-1.4-1.7-3-2.1.7-1-.7-.4-2.2h-3.4l-.4 2.2-1 .7-2.1-.7-1.7 3L9 10.8 8.9 12l.1 1.2-1.7 1.4 1.7 3 2.1-.7 1 .7.4 2.2h3.4l.4-2.2 1-.7 2.1.7 1.7-3-1.7-1.4.1-1.2Z",
  play: "M8 5.5v13l11-6.5-11-6.5Z",
  plus: "M12 5v14M5 12h14",
  check: "m5 13 4.5 4.5L19 7",
  close: "M6 6l12 12M18 6 6 18",
  chevronRight: "m9 6 6 6-6 6",
  chevronLeft: "m15 6-6 6 6 6",
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-14v5.2l3.4 2",
  refresh: "M20 12a8 8 0 1 1-2.6-5.9M20 4v4.5h-4.5",
  alert: "M12 8.5v5m0 3.2v.3M10.3 4.3 2.9 17.2A2 2 0 0 0 4.6 20h14.8a2 2 0 0 0 1.7-2.8L13.7 4.3a2 2 0 0 0-3.4 0Z",
  file: "M14 3v5h5M6 3h8l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z",
  trash: "M5 7h14M10 7V5h4v2m-7 0 .8 13h8.4L17 7",
  // Los que estrenó el sistema de cuentas.
  users: "M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm-6 9v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1m1-15.5a3.5 3.5 0 0 1 0 7M18 14h.5a4 4 0 0 1 4 4v2",
  list: "M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01",
  logout: "M15 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-3m3-8 4 4-4 4m4-4H9",
  key: "M15.5 10.5a4 4 0 1 0-3.6 4L13 16h2v2h2v2h3v-3.6l-4.6-4.6a4 4 0 0 0 .1-1.3ZM10 9.5h.01",
  copy: "M8 8V5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-3M5 8h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z",
  wrench: "M15.5 3.5a5.5 5.5 0 0 0-5 7.7L3.8 17.9a2 2 0 0 0 2.8 2.8l6.7-6.7a5.5 5.5 0 0 0 6.9-7.3l-3 3-2.9-2.9 3-3a5.5 5.5 0 0 0-1.8-.3Z",
  edit: "M4 20h4L20 8l-4-4L4 16v4Z",
  pause: "M9 5v14M15 5v14",
  bolt: "M13 3 5 14h6l-1 7 8-11h-6l1-7Z",
  sun: "M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0-14v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10 1.4 1.4m0-12.8-1.4 1.4m-10 10-1.4 1.4",
  moon: "M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z",
  calendar: "M4 9h16M8 3v3m8-3v3M5 6h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z",
  sparkle: "M12 3.5 13.8 9l5.5 1.8-5.5 1.8L12 18l-1.8-5.4L4.7 10.8 10.2 9 12 3.5Z",
  external: "M14 4h6v6M20 4l-8.5 8.5M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5",
  history: "M3.5 12a8.5 8.5 0 1 0 2.6-6.1M4 4v4.5h4.5M12 7.5V12l3 1.8",
  book: "M4 5h5.5a2.5 2.5 0 0 1 2.5 2.5V20a2.5 2.5 0 0 0-2.5-2.5H4V5Zm16 0h-5.5A2.5 2.5 0 0 0 12 7.5V20a2.5 2.5 0 0 1 2.5-2.5H20V5Z",
  folder: "M4 7.5A1.5 1.5 0 0 1 5.5 6h3.2l2 2H18.5A1.5 1.5 0 0 1 20 9.5v8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-10Z",
  archive: "M3.5 4.5h17v4h-17v-4Zm1.5 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-10M9.5 12h5",
  download: "M12 4v11m0 0 4-4m-4 4-4-4M5 19h14",
  arrowRight: "M4 12h15m0 0-5.5-5.5M19 12l-5.5 5.5",
  inbox: "M5 5h14l1.5 8.5V19a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-5.5L5 5Zm-1.5 8.5h4l1 2.5h7l1-2.5h4",
  package: "M12 3 4 7v10l8 4 8-4V7l-8-4Zm0 0v18M4 7l8 4 8-4",
  mic: "M12 4a2.8 2.8 0 0 1 2.8 2.8v5.4a2.8 2.8 0 0 1-5.6 0V6.8A2.8 2.8 0 0 1 12 4Zm7 8.2a7 7 0 0 1-14 0M12 19.2V22m-3.5 0h7",
  stop: "M8 8h8v8H8z",
  image: "M4 6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6Zm0 10 4.5-4.5 3 3 3.5-3.5L20 15M9 9.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z",
  globe: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-18c-2.5 2.3-3.8 5.4-3.8 9s1.3 6.7 3.8 9c2.5-2.3 3.8-5.4 3.8-9s-1.3-6.7-3.8-9ZM3.4 9h17.2M3.4 15h17.2",
  search: "M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13Zm4.8.3L20 21",
  printer: "M7 9V4h10v5M7 18H5.5A1.5 1.5 0 0 1 4 16.5v-5A1.5 1.5 0 0 1 5.5 10h13a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H17M7 15h10v5H7v-5Z",
  tool: "M14.5 3.5a4.5 4.5 0 0 0-5.9 5.7L3.6 14.2a2 2 0 0 0 2.8 2.8l5-5a4.5 4.5 0 0 0 5.7-5.9l-2.6 2.6-2.1-2.1 2.1-2.1ZM16 14l4.5 4.5a1.8 1.8 0 0 1-2.5 2.5L13.5 16.5",
  mail: "M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-11Zm0 .8 8 5.4 8-5.4",
  chart: "M4 20h16M7.5 20V11m4.5 9V5.5m4.5 14.5V14",
  chat: "M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9.5L5.5 20v-4H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm3.5 5.5h7",
  plug: "M9 3v5m6-5v5M6.5 8h11v3.5a5.5 5.5 0 0 1-11 0V8ZM12 17v4",
  more: "M5.5 12a1 1 0 1 0 0-.01M12 12a1 1 0 1 0 0-.01M18.5 12a1 1 0 1 0 0-.01",
  send: "M4 12 20 4l-6 16-3-7-7-1Zm7 1 9-9",
};

const path = computed(() => PATHS[props.name] ?? PATHS["sparkle"] ?? "");
const dimension = computed(() =>
  typeof props.size === "number" ? `${props.size}px` : props.size,
);
</script>

<template>
  <svg
    :width="dimension"
    :height="dimension"
    viewBox="0 0 24 24"
    fill="none"
    :stroke-width="strokeWidth"
    stroke="currentColor"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    class="shrink-0"
  >
    <path :d="path" />
  </svg>
</template>
